import { useCallback, useSyncExternalStore } from 'react'

import { quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { quantApi } from '@/app/lib/quant/quantApi'
import type { Strategy } from '@/app/lib/quant/types'
import { registerSessionReset } from '@/app/lib/sessionReset'

export type QuantStrategiesState =
  | { status: 'loading' }
  | { status: 'loaded'; strategies: Strategy[] }
  | { status: 'error'; message: string }

/* Module-level so the shell and the Strategies screen share one fetch. Loaded once per page load; Retry reloads. */
let state: QuantStrategiesState = { status: 'loading' }
let sequence = 0
let started = false
const listeners = new Set<() => void>()

function publish(next: QuantStrategiesState): void {
  state = next
  for (const listener of listeners) listener()
}

function load(silent = false): void {
  started = true
  sequence += 1
  const mine = sequence
  quantApi
    .listStrategies()
    .then((result) => {
      if (mine === sequence) publish({ status: 'loaded', strategies: result.strategies })
    })
    .catch((error: unknown) => {
      if (mine !== sequence) return
      // A silent refresh never replaces a list that is already on screen with an error.
      if (silent && state.status === 'loaded') return
      publish({ status: 'error', message: quantErrorMessage(error) })
    })
}

/** Re-requests the list without a loading flash (after a start). The loaded list stays on screen, even if this fails. */
export function refreshQuantStrategies(): void {
  load(true)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (!started) queueMicrotask(load)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): QuantStrategiesState {
  return state
}

export function resetQuantStrategies(): void {
  sequence += 1
  started = false
  listeners.clear()
  state = { status: 'loading' }
}

export function useQuantStrategies(): QuantStrategiesState & { retry: () => void } {
  const current = useSyncExternalStore(subscribe, getSnapshot)
  const retry = useCallback(() => {
    publish({ status: 'loading' })
    load()
  }, [])
  return { ...current, retry }
}

registerSessionReset(resetQuantStrategies)
